import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import {Button} from 'platform/components';

import {useId, useRef} from 'react';

import {isNotNil} from 'ramda';

type SortReplacementPopoverProps = {
  anchor: HTMLElement | null;
  returnFocusTo: HTMLElement;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onClose: () => void;
};

export function SortReplacementPopover(props: SortReplacementPopoverProps) {
  const messageId = useId();
  const hasFocusedRef = useRef(false);
  const restoreFocus = () => {
    if (props.returnFocusTo.isConnected) {
      props.returnFocusTo.focus();
    }
  };
  const close = () => {
    restoreFocus();
    props.onClose();
  };
  const {refs, floatingStyles, context} = useFloating({
    open: true,
    onOpenChange: (open, _event, reason) => {
      if (!open) {
        if (reason === 'escape-key') {
          restoreFocus();
        }
        props.onClose();
      }
    },
    elements: {reference: props.anchor},
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift({padding: 8})],
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, {role: 'dialog'});
  const {getFloatingProps} = useInteractions([dismiss, role]);

  return (
    <FloatingPortal preserveTabOrder={false}>
      <div
        ref={(node) => {
          refs.setFloating(node);
          if (isNotNil(node) && !hasFocusedRef.current) {
            node.querySelector('button')?.focus();
            hasFocusedRef.current = true;
          }
        }}
        style={floatingStyles}
        {...getFloatingProps()}
        aria-labelledby={messageId}
        className="z-(--z-dropdown-component) w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <p id={messageId} className="text-sm text-slate-700">
          {props.message}
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="secondary" size="small" title={props.cancelLabel} onClick={close} />
          <Button
            variant="primary"
            size="small"
            title={props.confirmLabel}
            onClick={() => {
              restoreFocus();
              props.onConfirm();
            }}
          />
        </div>
      </div>
    </FloatingPortal>
  );
}
